import { Dispatch, SetStateAction, useRef } from "react";
import { useTranslation } from "react-i18next";
import MaterialTable, { Column } from "@material-table/core";

import { Typography } from "@mui/material";
import { ProgramRole, TraineeType, User } from "../../API";
import {
  BulkInviteInvitationStatus,
  BulkInvitation,
  bulkInviteTableRowUpdate,
  bulkInviteTableRowDelete,
  bulkInviteRowRender,
  bulkInviteRowHasError,
  bulkInviteGetFormattedErrors,
  bulkInviteRowContainsExistingUserError
} from "./userInvitationUtils";
import { CognitoUserGroups } from "../Users/userUtils";
import { Regex } from "../../app/constants";
import { cloneDeep } from "lodash";
import { useAppDispatch } from "../../app/hooks";
import { setModalContent, setShowModal } from "../../common-ui/modalSlice";
import { AddUserToProgram } from "../Users/AddUserToProgram";

interface UserInvitationsBulkInviteTableProps {
  validatedInvitations: BulkInvitation[];
  currentInvitations: BulkInvitation[];
  setInvitations: Dispatch<SetStateAction<BulkInvitation[] | undefined>>;
  loading?: boolean;
  currentUserCognitoGroups?: CognitoUserGroups[];
  hasCustomProgramId?: boolean;
  statusMessages: Map<string, BulkInviteInvitationStatus | string>;
  setStatusMessages: Dispatch<SetStateAction<Map<string, BulkInviteInvitationStatus | string>>>;
  getUsersByEmail: (email: string) => Promise<User[] | null>;
  getUsersByNPI: (npi: number) => Promise<User[] | null>;
  selectedInstitutionId?: string;
  selectedProgramId?: string;
  onRowDelete?: (
    updatedInvitations: BulkInvitation[],
    deletedInvitation?: BulkInvitation,
    options?: { skipValidation?: boolean }
  ) => void;
  rowsAwaitingRevalidation: Set<string>;
  setRowsAwaitingRevalidation: Dispatch<SetStateAction<Set<string>>>;
}

export const UserInvitationsBulkInviteTable = ({
  setInvitations,
  loading,
  currentUserCognitoGroups,
  hasCustomProgramId,
  statusMessages,
  setStatusMessages,
  getUsersByEmail,
  getUsersByNPI,
  selectedInstitutionId,
  selectedProgramId,
  validatedInvitations,
  currentInvitations,
  onRowDelete,
  rowsAwaitingRevalidation,
  setRowsAwaitingRevalidation
}: UserInvitationsBulkInviteTableProps) => {
  const editableInvitations = cloneDeep(currentInvitations);
  const tableRef = useRef<MaterialTable<BulkInvitation>>(null);
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const columns: Array<
    Column<BulkInvitation & { tableData?: { editing: string } }>
  > = [
    {
      title: t("First Name"),
      field: "firstName",
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "firstName",
            editableInvitations,
            currentInvitations
          ) &&
            row.firstName.length > 0 &&
            row.firstName.length <= 40),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "firstName",
          "Required, 1-40 chars.",
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("Last Name"),
      field: "lastName",
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "lastName",
            editableInvitations,
            currentInvitations
          ) &&
            row.lastName.length > 0 &&
            row.lastName.length <= 40),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "lastName",
          "Required, 1-40 chars.",
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("Email"),
      field: "email",
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "email",
            editableInvitations,
            currentInvitations
          ) &&
            row.email.length !== 0 &&
            Regex.Email.test(row.email)),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "email",
          "Required, valid email.",
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("NPI"),
      field: "npi",
      validate: (row) => {
        const npiRequired = [
          ProgramRole.Attending.toString(),
          ProgramRole.Trainee.toString()
        ].includes(row.role);
        return {
          // required if: ([ProgramRole.Attending, ProgramRole.Trainee].includes(row.programRole))
          // not allowed if: Other.
          isValid:
            row.tableData?.editing === "delete" ||
            (!bulkInviteRowHasError(
              row,
              "npi",
              editableInvitations,
              currentInvitations
            ) &&
              (npiRequired
                ? !!(row.npi && row.npi >= 1000000000 && row.npi <= 2999999999)
                : !row.npi)),
          helperText: bulkInviteGetFormattedErrors(
            row,
            "npi",
            npiRequired
              ? "Required, number between 1000000000 and 2999999999"
              : "(do not use for this role)",
            editableInvitations,
            currentInvitations
          )
        };
      }
    },
    {
      title: t("SIMPL LearnID"),
      field: "simplLearnId",
      cellStyle: {
        maxWidth: "150px",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }
    },
    {
      title: t("PGY"),
      field: "pgy",
      validate: (row) => {
        const pgyRequired = row.role === ProgramRole.Trainee;
        return {
          isValid:
            row.tableData?.editing === "delete" ||
            (!bulkInviteRowHasError(
              row,
              "pgy",
              editableInvitations,
              currentInvitations
            ) &&
              (pgyRequired
                ? !!(row.pgy && row.pgy >= 1 && row.pgy <= 12)
                : !row.pgy)),
          helperText: bulkInviteGetFormattedErrors(
            row,
            "pgy",
            pgyRequired
              ? "Required, number from 1-12"
              : "(do not use for this role)",
            editableInvitations,
            currentInvitations
          )
        };
      }
    },
    {
      title: t("Program ID (optional)"),
      field: "programId",
      hidden:
        !hasCustomProgramId &&
        !currentUserCognitoGroups?.includes(CognitoUserGroups.SysAdmins),
      validate: (row) => {
        return {
          isValid:
            row.tableData?.editing === "delete" ||
            !bulkInviteRowHasError(
              row,
              "programId",
              editableInvitations,
              currentInvitations
            ),
          helperText: bulkInviteGetFormattedErrors(
            row,
            "programId",
            "Overwrites the program selected above.",
            editableInvitations,
            currentInvitations
          )
        };
      }
    },
    {
      title: t("Role"),
      field: "role",
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "role",
            editableInvitations,
            currentInvitations
          ) &&
            !!(
              row.role &&
              [
                ProgramRole.Trainee,
                ProgramRole.Attending,
                ProgramRole.ProgramAdmin
              ].some((allowedRole) => allowedRole === row.role)
            )),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "role",
          `Must be "Trainee", "Attending", or "ProgramAdmin"`,
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("Trainee Type"),
      field: "traineeType",
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "traineeType",
            editableInvitations,
            currentInvitations
          ) &&
            !(
              row.role === ProgramRole.Trainee &&
              ![
                TraineeType.Categorical,
                TraineeType.DesignatedPreliminary,
                TraineeType.NonDesignatedPreliminary,
                TraineeType.Fellow
              ].some((allowedType) => allowedType === row.traineeType)
            )),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "traineeType",
          row.role === ProgramRole.Trainee
            ? "Must be one of: Categorical, DesignatedPreliminary, NonDesignatedPreliminary, Fellow"
            : "(do not use for this role)",
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("Job Title"),
      field: "jobTitle",
      cellStyle: {
        maxWidth: "150px",
        overflow: "hidden",
        textOverflow: "ellipsis"
      },
      validate: (row) => ({
        isValid:
          row.tableData?.editing === "delete" ||
          (!bulkInviteRowHasError(
            row,
            "jobTitle",
            editableInvitations,
            currentInvitations
          ) &&
            (row.jobTitle === undefined ||
              row.jobTitle === "" ||
              row.jobTitle.length <= 40)),
        helperText: bulkInviteGetFormattedErrors(
          row,
          "jobTitle",
          "Optional, max 40 characters.",
          editableInvitations,
          currentInvitations
        )
      })
    },
    {
      title: t("Invitation Status"),
      field: "invitationStatus",
      editable: "never",
      render: (row) => {
        const { status, color } = bulkInviteRowRender(
          row,
          currentInvitations,
          validatedInvitations,
          statusMessages
        );
        return (
          <Typography color={color} fontWeight="bold">
            {t(status)}
          </Typography>
        );
      }
    }
  ];

  return (
    <MaterialTable
      tableRef={tableRef}
      columns={columns}
      data={editableInvitations}
      options={{
        showTitle: false,
        pageSize: 1000,
        pageSizeOptions: [5, 10, 20, 1000],
        emptyRowsWhenPaging: false
      }}
      isLoading={loading}
      components={{
        Toolbar: (props) => null
      }}
      actions={[
        (rowData) => {
          const hasExistingUserError = bulkInviteRowContainsExistingUserError(
            rowData,
            statusMessages
          );
          const deleteRowAction = (options?: { skipValidation?: boolean }) => {
            bulkInviteTableRowDelete({
              currentInvitations,
              targetRow: rowData,
              setInvitations,
              statusMessages,
              setStatusMessages,
              onDelete: onRowDelete,
              setRowsAwaitingRevalidation,
              skipValidationOnDelete: options?.skipValidation
            });
          };
          return {
            icon: "add",
            tooltip: "Add existing User to Program.",
            onClick: async (event, rowData) => {
              if (!Array.isArray(rowData)) {
                let usersWithNPI: User[] | null = null;
                const usersWithEmail = await getUsersByEmail(rowData.email);
                if (rowData.npi) {
                  usersWithNPI = await getUsersByNPI(rowData.npi);
                }
                const usersAreSame = !!(
                  usersWithEmail &&
                  usersWithNPI &&
                  usersWithEmail.every(
                    (userWithEmail) =>
                      !!usersWithNPI?.find(
                        (userWithNPI) => userWithNPI.id === userWithEmail.id
                      )
                  )
                );
                const removeAwaitingRevalidation = () => {
                  if (rowData?.requestId) {
                    setRowsAwaitingRevalidation((prev) => {
                      if (!prev.has(rowData.requestId!)) return prev;
                      const updated = new Set(prev);
                      updated.delete(rowData.requestId!);
                      return updated;
                    });
                  }
                };
                // Dev Note: I left this in place since dispatch needs to be called from inside of a component.
                dispatch(
                  setModalContent(
                    <AddUserToProgram
                      existingUsersWithEmail={usersWithEmail}
                      existingUsersWithNPI={
                        usersAreSame ? undefined : usersWithNPI
                      }
                      selectedProgramRole={rowData.role}
                      selectedProgramTraineeType={
                        rowData.traineeType as TraineeType
                      }
                      selectedNPI={rowData.npi}
                      selectedInstitutionId={selectedInstitutionId}
                      selectedSimplLearnId={rowData.simplLearnId}
                      selectedProgramId={selectedProgramId}
                      selectedPGY={rowData.pgy}
                      postSuccessAction={() => {
                        removeAwaitingRevalidation();
                        deleteRowAction({ skipValidation: true });
                        dispatch(setShowModal(false));
                      }}
                      deleteRow={() => {
                        removeAwaitingRevalidation();
                        deleteRowAction({ skipValidation: true });
                      }}
                      isBulkAddition={true}
                    />
                  )
                );
                dispatch(setShowModal(true));
              }
            },
            hidden: !hasExistingUserError,
            disabled: loading
          };
        }
      ]}
      editable={{
        isEditHidden: (rowData) =>
          bulkInviteRowContainsExistingUserError(rowData, statusMessages),
        isEditable: () => !loading,
        isDeletable: () => !loading,
        onRowAdd: (newRow) => {
          return new Promise<void>((resolve) => {
            setInvitations([...currentInvitations, newRow] as BulkInvitation[]);
            resolve();
          });
        },
        onRowUpdate: (newData, oldData) => {
          return new Promise<void>((resolve) => {
            bulkInviteTableRowUpdate({
              currentInvitations,
              oldData,
              newData,
              setInvitations,
              statusMessages,
              setStatusMessages
            });
            resolve();
          });
        },
        onRowDelete: (oldData) => {
          return new Promise<void>((resolve) => {
            bulkInviteTableRowDelete({
              currentInvitations,
              targetRow: oldData,
              setInvitations
            });
            resolve();
          });
        }
      }}
    />
  );
};
